import React, { useRef, useEffect, useState, useContext } from "react";
import { useLocation } from "@reach/router";
import CloseIcon from "../../assets/svgs/visual-search-close.svg";
import FileUploadIcon from "../../assets/svgs/file-upload.svg";
import axios from "axios";
import Results from "./results";
import VisualLoading from "./VisualLoading";
import { resizeToDFromDataURL } from "@root/helpers/resize-image";
import { VisualSearchContext } from '@root/context/visual-search';

const Camera = ({ close, initialPhoto = null }) => {
    const { visualSearchState, saveVisualSearchState, clearVisualSearchState } = useContext(VisualSearchContext) || {};
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const fileInputRef = useRef(null);
    const [isCameraReady, setIsCameraReady] = useState(false);
    const [hasCameraPermission, setHasCameraPermission] = useState(true); // 1. New state for permission
    const [screenDimensions, setScreenDimensions] = useState({ width: 640, height: 480 });
    const [photo, setPhoto] = useState(initialPhoto || visualSearchState?.photo);
    const [showCaptureEffect, setShowCaptureEffect] = useState(false);
    const [results, setResults] = useState(visualSearchState?.results || {});
    const [isLoading, setIsLoading] = useState(visualSearchState?.isLoading || false);
    const [isError, setIsError] = useState(visualSearchState?.isError || false);
    const [isUploadedImage, setIsUploadedImage] = useState(!!initialPhoto || visualSearchState?.isUploadedImage);
    const location = useLocation();

    useEffect(() => {
        if (window) {
            setScreenDimensions({ width: window.innerWidth, height: window.innerHeight });
        }
    }, []);

    // ... (Existing useEffects for state restoration, location change, and drawToCanvas remain unchanged) ...

    // Check for saved state on mount from context
    useEffect(() => {
        const photoToUse = initialPhoto || visualSearchState?.photo;

        if (photoToUse && !isCameraReady) {
            setPhoto(photoToUse);
            setIsUploadedImage(!!initialPhoto || !!visualSearchState?.isUploadedImage);

            // If we have saved results, restore them; otherwise process the image
            if (visualSearchState?.results && Object.keys(visualSearchState.results).length > 0) {
                setResults(visualSearchState.results);
                setIsLoading(false);
                setIsError(false);
            } else {
                // Process the image if we don't have saved results
                // This handles both initialPhoto (uploaded file) and restored photos
                processImage(photoToUse);
            }

            setIsCameraReady(true);
        } else if (!initialPhoto && !visualSearchState?.photo) {
            // If no saved state and no initial photo, start camera
            setIsCameraReady(true);
        }
    }, [initialPhoto]);

    // Check for saved state on mount and when location changes (back navigation)
    useEffect(() => {
        // Check if we're on the path where camera was opened and have saved state
        const currentPath = location.pathname;
        const savedPath = visualSearchState?.sourcePath;

        if (visualSearchState?.photo && savedPath === currentPath) {
            // We're back on the same path where camera was opened, restore it
            setPhoto(visualSearchState.photo);
            setIsUploadedImage(!!initialPhoto || !!visualSearchState?.isUploadedImage);
            setIsCameraReady(true);
        } else if (savedPath && savedPath !== currentPath) {
            // We navigated away from the saved path, don't restore
            // (but keep the state in case user navigates back)
        }
    }, [location.pathname, visualSearchState?.photo, visualSearchState?.sourcePath, initialPhoto]);


    // Initialize camera stream on component mount and clean up on unmount
    useEffect(() => {
        // Skip camera initialization if we have an uploaded image
        if (initialPhoto) {
            return;
        }

        const startCamera = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: 'environment' // Use rear camera
                    }
                });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                    setHasCameraPermission(true); // Access granted
                }
            } catch (err) {
                console.error('Error accessing camera:', err);
                setHasCameraPermission(false); // 2. Access denied: set state to false
            }
        };

        startCamera();

        return () => {
            if (videoRef.current?.srcObject) {
                videoRef.current.srcObject.getTracks().forEach(track => track.stop());
            }
            cancelAnimationFrame(animationRef.current);
        };
    }, [initialPhoto]);


    // Draw video to canvas
    useEffect(() => {
        // Skip canvas drawing if we have an uploaded image
        if (initialPhoto) {
            return;
        }

        const drawToCanvas = () => {
            const video = videoRef.current;
            const canvas = canvasRef.current;

            if (video && canvas && video.videoWidth > 0 && video.videoHeight > 0) {
                const ctx = canvas.getContext('2d');

                ctx.fillStyle = 'black';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                const videoAspectRatio = video.videoWidth / video.videoHeight;
                const canvasAspectRatio = canvas.width / canvas.height;

                let drawWidth, drawHeight, offsetX, offsetY;

                if (videoAspectRatio > canvasAspectRatio) {
                    drawHeight = canvas.height;
                    drawWidth = drawHeight * videoAspectRatio;
                    offsetX = (canvas.width - drawWidth) / 2;
                    offsetY = 0;
                } else {
                    drawWidth = canvas.width;
                    drawHeight = drawWidth / videoAspectRatio;
                    offsetX = 0;
                    offsetY = (canvas.height - drawHeight) / 2;
                }

                ctx.save();
                ctx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
                ctx.restore();
            }

            animationRef.current = requestAnimationFrame(drawToCanvas);
        };

        const startDrawing = () => {
            if (videoRef.current && videoRef.current.readyState >= 2) {
                drawToCanvas();
            } else {
                setTimeout(startDrawing, 100);
            }
        };

        // Only start drawing if permission is granted (to prevent trying to draw from a non-existent stream)
        if (hasCameraPermission) {
            startDrawing();
            setIsCameraReady(true);
        } else {
            // If permission is denied, we still want the component to be ready to show the controls
            setIsCameraReady(true);
        }
    }, [initialPhoto, hasCameraPermission]); // Add hasCameraPermission as dependency

    // ... (Existing useEffects for resize, live chat widget, key down, and state saving remain unchanged) ...

    useEffect(() => {
        const handleResize = () => {
            setScreenDimensions({ width: window.innerWidth, height: window.innerHeight });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // hide live chat widget opener when camera is open
    useEffect(() => {
        const widgetOpener = document.getElementById('chat-widget-minimized');
        if (widgetOpener) {
            widgetOpener.style.display = 'none';
        }

        return () => {
            if (widgetOpener) {
                widgetOpener.style.display = 'block';
            }
        }
    }, []);

    // Handle key down
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [close]);

    useEffect(() => {
        if (photo || Object.keys(results).length > 0) {
            const currentPath = location.pathname;
            const pathToSave = visualSearchState?.sourcePath || currentPath;

            saveVisualSearchState?.({
                photo,
                results,
                isLoading,
                isError,
                isUploadedImage,
                conversationId: results?.conversationId,
                sourcePath: pathToSave
            });
        }
    }, [photo, results, isLoading, isError, isUploadedImage, saveVisualSearchState, location.pathname, visualSearchState?.sourcePath]);

    const processImage = async (dataURL) => {
        setIsLoading(true);
        setIsError(false);

        try {
            // Resize image
            const resizedImage = await resizeToDFromDataURL(dataURL);
            const images = [dataURL.split(',')[1]];

            // Get search results
            const res = await getSearchResults({ images: images });

            if (!res) {
                setIsError(true);
                setIsLoading(false);
                return;
            }

            setResults(res);
            setIsLoading(false);
        } catch (err) {
            setIsError(true);
            setIsLoading(false);
            console.error('Error processing image:', err);
        }
    };

    const handleRetryClick = async () => {
        clearVisualSearchState?.();
        setPhoto(null);
        setResults({});
        setIsError(false);
        setIsLoading(false);
        setShowCaptureEffect(false);

        // If it was an uploaded image, close the modal to allow re-upload
        if (isUploadedImage) {
            close();
            return;
        }

        // 3. Logic to retry camera access
        try {
            // Stop existing stream if any
            if (videoRef.current?.srcObject) {
                videoRef.current.srcObject.getTracks().forEach(track => track.stop());
            }

            // Request new camera stream
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment'
                }
            });

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
                setHasCameraPermission(true); // Success: update permission state
            }
        } catch (err) {
            console.error('Error restarting camera:', err);
            setHasCameraPermission(false); // Failure: keep permission state false
            // Note: Since permission is still false, the user must go to browser settings to enable it.
        }
    }

    const getSearchResults = async (data) => {
        try {
            const res = await axios.post(`${process.env.GATSBY_AI_URL}/api/v1/product-selection/conversation/start`, {
                ...data
            });

            const products = res?.data?.predictions?.products || [];
            const categories = res?.data?.predictions?.categories || [];
            const shapes = res?.data?.predictions?.custom_covers || [];

            if ([...products, ...categories, ...shapes].length === 0) {
                setIsError(true);
                return null;
            }
            return { products, categories, shapes, conversationId: res?.data?.conversation_id };
        } catch (err) {
            console.error('Error searching image:', err);
            setIsError(true);
            return null;
        }
    }

    const takePicture = async () => {
        // Only allow picture if we have permission
        if (!hasCameraPermission) return;

        setShowCaptureEffect(true);
        setTimeout(() => {
            setShowCaptureEffect(false);
        }, 100);

        const canvas = canvasRef.current;
        const dataURL = canvas.toDataURL('image/jpeg');
        setPhoto(dataURL);
        await processImage(dataURL);
    }

    const stopCameraStream = () => {
        if (videoRef.current?.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(track => track.stop());
        }
        cancelAnimationFrame(animationRef.current);
    };

    const handleFileUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            event.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const dataURL = e.target.result;
            stopCameraStream();
            setIsUploadedImage(true);
            setPhoto(dataURL);
            processImage(dataURL);
        };
        reader.onerror = () => {
            event.target.value = '';
        };
        reader.readAsDataURL(file);
    };

    if (!isCameraReady) {
        return null;
    }

    const handleClose = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        clearVisualSearchState?.();
        close();
    };

    const renderCameraControls = () => {
        // If we have a photo, or if we have results/error/loading, don't show live camera controls
        if (photo || Object.keys(results).length > 0 || isError || isLoading) {
            return null;
        }

        if (hasCameraPermission) {
            // 4A. Show Shutter Button (Access Granted)
            return (
                <div className="camera__controls">
                    <p>
                        Focus on a single object, well-lit <br />
                        and from a slight angle.
                    </p>
                    <div className="camera__actions">
                        <div
                            className="camera__actions-upload"
                            onClick={handleFileUploadClick}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && handleFileUploadClick()}
                            aria-label="Upload image"
                        >
                            <img src={FileUploadIcon} alt="" />
                        </div>
                        <div className="camera__shutter">
                            <div onClick={takePicture} className={`camera__shutter-button-container ${showCaptureEffect ? 'camera__shutter-button-container--clicked' : ''}`}>
                                <div className="camera__shutter-button"></div>
                            </div>
                        </div>
                        <div></div>
                    </div>
                </div>
            )
        } else {
            // 4B. Show "Access Denied" Message and Retry Button (Access Denied)
            return (
                <div className="camera__controls camera__denied-message">
                    <p>
                        Camera access is required for this feature.<br />
                    </p>
                    <div onClick={handleRetryClick} className="camera__no-permission">
                        Retry Camera Access
                    </div>
                </div>
            )
        }
    }

    return (
        <div className="image-search__camera">
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="camera__file-input"
                aria-hidden="true"
                tabIndex={-1}
            />
            {showCaptureEffect && (
                <div className="camera__capture-effect">
                </div>
            )}
            {photo && (
                <div className="camera__photo">
                    <img src={photo} alt="photo" />
                    <img src={photo} alt="photo" className="camera__photo-image--blured" />
                </div>
            )}

            {!isLoading && Object.keys(results).length > 0 && (
                <Results results={results} photo={photo} close={handleClose} />
            )}
            {isError && (
                <div className="camera__error">
                    <div className="camera__error-text">
                        Something went wrong, please try again.
                    </div>

                    <div onClick={() => handleRetryClick()} className="camera__results-contact">
                        Retry
                    </div>
                </div>
            )}
            {isLoading && (
                <VisualLoading />
            )}
            <div className="camera__header">
                <div />
                <div onClick={handleClose} className="camera__header-close">
                    <img src={CloseIcon} alt="close" />
                </div>
            </div>

            {!photo && (
                <>
                    {/* Video and canvas elements remain, but only the canvas is visible.
                        The rendering logic (drawToCanvas) now depends on hasCameraPermission. */}
                    <video ref={videoRef} style={{ display: 'none' }} />
                    <canvas ref={canvasRef} width={screenDimensions.width} height={screenDimensions.height} />

                    {/* Conditional rendering for controls */}
                    {renderCameraControls()}
                </>
            )}
        </div>
    )
}

export default Camera;